import { Button, Chip, DataTable, Page, Tooltip } from '@dixa/lib-shared-habla';

import { WebformEndpoint, webformEndpointsApi } from 'apis/webformEndpoints';
import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { CreateFormEndpointModal } from './createFormEndpointModal';
import { DeleteFormEndpointModal } from './deleteFormEndpointModal';
import styles from './webformEndpointsSection.module.scss';
import { WebformEndpointsTableActions } from './webformEndpointsTableActions';

export const pathToWebformEndpointsSettings =
  '/settings/integrations/webform-endpoints';

const emptyArray: WebformEndpoint[] = [];

type Props = {
  formId: string;
};

export const WebformEndpointsSection = ({ formId }: Props): JSX.Element => {
  const dispatch = useAppDispatch();

  const { webformEndpoints, isLoading, isError, isSuccess, refetch } =
    webformEndpointsApi.useReadWebformEndpointsQuery(
      { formIntegrationId: formId },
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          webformEndpoints: data?.data.endpoints || emptyArray,
        }),
      },
    );

  const handleDeleteEndpoint = (endpoint: WebformEndpoint) => () =>
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:deleteFormEndpoint',
          arg: { endpoint },
        },
      }),
    );

  const handleCreateEndpoint = () =>
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:createFormEndpoint',
          arg: { formId },
        },
      }),
    );

  const createEndpointButton = (
    <Button onClick={handleCreateEndpoint}>Create endpoint</Button>
  );

  return (
    <Page.Section title="Form endpoints" controls={createEndpointButton}>
      <DataTable<WebformEndpoint, never>
        data={webformEndpoints}
        tableStyleProps={{
          stickyHeader: true,
        }}
        tableStatusProps={{
          loading: isLoading,
          loadingFailed: isError,
          onLoadingRetry: refetch,
          emptyState:
            !webformEndpoints.length && isSuccess
              ? {
                  icon: null,
                  title: 'Webform endpoint',
                  description: createEndpointButton,
                }
              : undefined,
        }}
        columns={[
          {
            label: 'Name',
            selector: ({ description }) => description,
          },
          {
            label: 'Email',
            selector: ({ email_integration_id }) => email_integration_id,
          },
          {
            label: 'Url',
            selector: ({ endpoint_uri, redirect_uri }) => (
              <Tooltip
                content={
                  <>
                    {endpoint_uri}
                    {redirect_uri && <div>Redirect URL: {redirect_uri}</div>}
                  </>
                }
              >
                <Chip className={styles.url}>{endpoint_uri}</Chip>
              </Tooltip>
            ),
          },
          {
            label: undefined,
            style: {
              width: 0,
            },
            selector: (endpoint) => (
              <WebformEndpointsTableActions
                endpointUrl={endpoint.endpoint_uri}
                handleDeleteWebformEndpoint={handleDeleteEndpoint(endpoint)}
              />
            ),
          },
        ]}
      />
      <CreateFormEndpointModal />
      <DeleteFormEndpointModal />
    </Page.Section>
  );
};
